import {
  Color,
  Mesh,
  SRGBColorSpace,
  Vector3,
  type BufferAttribute,
  type InterleavedBufferAttribute,
  type Material,
  type Object3D,
  type Texture,
} from 'three'

import {
  ACCENT_HUE_BINS,
  ACCENT_LIGHTNESS,
  ACCENT_MAX_SAMPLED_TRIANGLES,
  ACCENT_MAX_SAMPLED_VERTICES,
  ACCENT_MIN_SATURATION,
  ACCENT_SATURATION,
  ACCENT_TEXTURE_SAMPLE_PX,
} from '../config'
import type {PlacementConfig} from '../content/manifest.types'
import {describeError} from '../util/errors'

/**
 * Derives the entrance effect's accent colour from the model's own materials.
 *
 * Read-only, and deliberately outside `src/effects/`: the effect's contract is
 * that it never looks at the model, so the looking happens here and it is handed
 * a finished `Color`.
 *
 * ONLY THE HUE COMES FROM THE MODEL. Saturation and lightness are forced to
 * constants. Measured on the reference GLB, both alternatives fail:
 *
 *   - averaging the materials gives #b4bbd3, a muddy grey-blue, because a white
 *     body and a navy outline cancel;
 *   - using the extracted colour as-is gives #004ea2, whose relative luminance
 *     is 0.081 - under additive blending over a camera feed, invisible.
 *
 * WEIGHT IS SATURATION x AREA, and the saturation factor does the real work.
 * Toon models are commonly a body plus an inverted-hull outline shell, and the
 * shell has MORE surface area than the body it wraps - so a plain area-weighted
 * average is dragged toward the outline. Weighting by saturation sidesteps that
 * entirely: an achromatic body scores zero and drops out, with no "skip the
 * white parts" special case to get wrong.
 *
 * Area rather than triangle count, because polygon density is an authoring
 * choice: a low-poly backdrop and a densely subdivided prop should not be ranked
 * by how finely someone modelled them.
 *
 * Hues are binned and then averaged WITHIN the heaviest bin only. A circular
 * mean over everything would happily return a hue the model does not contain
 * (average red and green, get yellow).
 */

export type ColorWarning =
  | {readonly kind: 'noColoredMaterials'}
  | {readonly kind: 'compressedTexture'; readonly material: string}
  | {readonly kind: 'textureReadBlocked'; readonly material: string; readonly reason: string}
  | {readonly kind: 'trianglesSampled'; readonly total: number; readonly sampled: number}

/** One material's contribution. Reported so a surprising result can be traced. */
export interface AccentSample {
  readonly material: string
  /** sRGB hex of the material's effective base colour, without the leading `#`. */
  readonly hex: string
  /** 0..1, in sRGB space. */
  readonly hue: number
  readonly saturation: number
  readonly weight: number
}

export interface ResolvedAccent {
  /** In the renderer's working colour space. Ready to hand to the effect. */
  readonly color: Color
  readonly source: 'model' | 'manifest'
  readonly samples: readonly AccentSample[]
  readonly warnings: readonly ColorWarning[]
}

export const describeColorWarning = (warning: ColorWarning): string => {
  switch (warning.kind) {
    case 'noColoredMaterials':
      return 'no material has usable saturation; fell back to placement.accentColor'
    case 'compressedTexture':
      return `${warning.material}: compressed texture cannot be read back; used its base colour factor only`
    case 'textureReadBlocked':
      return `${warning.material}: texture pixels unreadable (${warning.reason}); used its base colour factor only`
    case 'trianglesSampled':
      return `area weighting sampled ${warning.sampled} of ${warning.total} triangles`
  }
}

type VertexAttribute = BufferAttribute | InterleavedBufferAttribute

/** Anything carrying a `.color`. `Material` itself has none - MeshBasicMaterial and friends do. */
type ColoredMaterial = Material & {readonly color: Color; readonly map?: Texture | null}

interface MaterialUsage {
  area: number
  /** First `COLOR_0` seen for this material, if any. */
  vertexColor: VertexAttribute | null
}

const hasColor = (material: Material): material is ColoredMaterial =>
  (material as {color?: {isColor?: boolean}}).color?.isColor === true

const isCompressed = (texture: Texture): boolean =>
  (texture as {isCompressedTexture?: boolean}).isCompressedTexture === true

const materialsOf = (mesh: Mesh): readonly Material[] =>
  Array.isArray(mesh.material) ? mesh.material : [mesh.material]

const triangleCount = (mesh: Mesh): number => {
  const position = mesh.geometry.getAttribute('position')
  if (position === undefined) return 0
  return Math.floor((mesh.geometry.index?.count ?? position.count) / 3)
}

/**
 * Mean colour of a base-colour texture in the working space, or a reason it
 * could not be read.
 *
 * Drawn down to a small square first so the browser's own box filter does the
 * averaging - reading a full-size image back costs far more for no better
 * answer. Not scaled to 1x1: `imageSmoothingQuality` varies enough between
 * engines to make that unstable.
 */
const averageTexture = (
  texture: Texture
): {readonly color: Color} | {readonly blocked: string} | null => {
  // CompressedTexture.image carries only {width, height} - there are no pixels
  // to read. Transcoded KTX2 is still wrapped in one, so there is no reliable
  // path here at all; the caller reports this separately.
  if (isCompressed(texture)) return null

  const image = texture.image
  if (!(image instanceof ImageBitmap) && !(image instanceof HTMLImageElement)) return null

  try {
    const size = ACCENT_TEXTURE_SAMPLE_PX
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const context = canvas.getContext('2d', {willReadFrequently: true})
    if (context === null) return null

    context.drawImage(image, 0, 0, size, size)
    // GLB-embedded images arrive as blob: URLs and are same-origin, but a .gltf
    // pointing at another host taints the canvas and this throws SecurityError.
    const {data} = context.getImageData(0, 0, size, size)

    let r = 0
    let g = 0
    let b = 0
    let count = 0
    for (let i = 0; i < data.length; i += 4) {
      // Skip transparent texels: atlas padding is usually black or white, and
      // averaging it in pulls every result toward grey.
      if ((data[i + 3] ?? 0) < 8) continue
      r += data[i] ?? 0
      g += data[i + 1] ?? 0
      b += data[i + 2] ?? 0
      count++
    }
    if (count === 0) return null

    // Canvas bytes are sRGB-encoded; GLTFLoader tags the base colour map
    // SRGBColorSpace. This is the one place a conversion is needed.
    const color = new Color().setRGB(r / count / 255, g / count / 255, b / count / 255, SRGBColorSpace)
    return {color}
  } catch (error: unknown) {
    return {blocked: describeError(error)}
  }
}

/**
 * Mean vertex colour, already in the working space (glTF vertex colours are
 * linear), or `null`.
 *
 * Read through `getX/getY/getZ` rather than `.array`: that transparently handles
 * normalised Uint8/Uint16 storage and interleaved buffers, and sits outside
 * `noUncheckedIndexedAccess`.
 */
const averageVertexColor = (attribute: VertexAttribute): Color | null => {
  const total = attribute.count
  if (total === 0) return null
  const stride = Math.max(1, Math.ceil(total / ACCENT_MAX_SAMPLED_VERTICES))

  let r = 0
  let g = 0
  let b = 0
  let count = 0
  for (let i = 0; i < total; i += stride) {
    r += attribute.getX(i)
    g += attribute.getY(i)
    b += attribute.getZ(i)
    count++
  }
  return new Color(r / count, g / count, b / count)
}

/** Summed triangle area in world units. `stride` skips triangles on huge meshes. */
const surfaceArea = (mesh: Mesh, stride: number): number => {
  const position = mesh.geometry.getAttribute('position')
  if (position === undefined) return 0
  const triangles = triangleCount(mesh)
  if (triangles === 0) return 0
  const index = mesh.geometry.index

  const a = new Vector3()
  const b = new Vector3()
  const c = new Vector3()
  const ab = new Vector3()
  const ac = new Vector3()

  let area = 0
  let sampled = 0
  for (let t = 0; t < triangles; t += stride) {
    const base = t * 3
    a.fromBufferAttribute(position, index?.getX(base) ?? base)
    b.fromBufferAttribute(position, index?.getX(base + 1) ?? base + 1)
    c.fromBufferAttribute(position, index?.getX(base + 2) ?? base + 2)
    area += ab.subVectors(b, a).cross(ac.subVectors(c, a)).length() * 0.5
    sampled++
  }
  if (sampled === 0) return 0

  // Scale back up for the skipped triangles, then into world units. Geometry is
  // in local space and normalize.ts has already refreshed matrixWorld by the
  // time this runs. A SkinnedMesh is measured in its bind pose, which is close
  // enough for a relative weight.
  const scale = mesh.matrixWorld.getMaxScaleOnAxis()
  return ((area * triangles) / sampled) * scale * scale
}

export const resolveAccentColor = (root: Object3D, config: PlacementConfig): ResolvedAccent => {
  const warnings: ColorWarning[] = []
  const samples: AccentSample[] = []

  const fallback = (): ResolvedAccent => ({
    color: new Color(config.accentColor),
    source: 'manifest',
    samples,
    warnings,
  })

  // Nothing will look at the result, so skip the traversal entirely. Checking
  // the effect kind here rather than at the call site keeps the caller from
  // having to carry a conditionally-absent colour around.
  if (config.effect === 'none') return fallback()
  if (config.accentColorSource === 'manifest') return fallback()

  // One stride for the whole model, so meshes stay comparable to each other.
  let totalTriangles = 0
  root.traverse((object) => {
    if (object instanceof Mesh) totalTriangles += triangleCount(object)
  })
  const stride = Math.max(1, Math.ceil(totalTriangles / ACCENT_MAX_SAMPLED_TRIANGLES))
  if (stride > 1) {
    warnings.push({
      kind: 'trianglesSampled',
      total: totalTriangles,
      sampled: Math.ceil(totalTriangles / stride),
    })
  }

  // Keyed by material, so one shared across several meshes accumulates a single
  // weight instead of competing with itself.
  const usage = new Map<ColoredMaterial, MaterialUsage>()
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return
    const area = surfaceArea(object, stride)
    if (area <= 0) return
    // GLTFLoader emits one primitive per Mesh, so the array case is rare - but
    // it costs nothing to survive. Draw groups are not resolved, so each
    // material is credited the whole mesh; fine for a relative weight.
    const vertexColor = object.geometry.getAttribute('color') ?? null
    for (const material of materialsOf(object)) {
      if (!hasColor(material)) continue
      const existing = usage.get(material)
      if (existing === undefined) usage.set(material, {area, vertexColor})
      else {
        existing.area += area
        existing.vertexColor ??= vertexColor
      }
    }
  })

  const hsl = {h: 0, s: 0, l: 0}
  const binWeight = new Array<number>(ACCENT_HUE_BINS).fill(0)
  const binHue = new Array<number>(ACCENT_HUE_BINS).fill(0)

  for (const [material, {area, vertexColor}] of usage) {
    // glTF base colour is factor x texture x vertex colour. `material.color` is
    // the factor and is ALREADY in the working space - GLTFLoader writes it with
    // setRGB(..., LinearSRGBColorSpace) - so it needs no conversion.
    const effective = material.color.clone()

    const map = material.map
    if (map != null) {
      const averaged = averageTexture(map)
      if (averaged === null && isCompressed(map)) {
        warnings.push({kind: 'compressedTexture', material: material.name})
      } else if (averaged !== null && 'blocked' in averaged) {
        warnings.push({kind: 'textureReadBlocked', material: material.name, reason: averaged.blocked})
      } else if (averaged !== null) {
        effective.multiply(averaged.color)
      }
    }

    // Exporters usually leave the factor at white when vertex colours carry the
    // real colour, so reading only `.color` would score the material achromatic
    // and silently drop it.
    if (material.vertexColors && vertexColor !== null) {
      const averaged = averageVertexColor(vertexColor)
      if (averaged !== null) effective.multiply(averaged)
    }

    // The second argument is load-bearing: the default is the working space
    // (srgb-linear), and HSL taken there is not the HSL anyone means.
    effective.getHSL(hsl, SRGBColorSpace)
    const weight = hsl.s * area
    samples.push({
      material: material.name,
      hex: effective.getHexString(SRGBColorSpace),
      hue: hsl.h,
      saturation: hsl.s,
      weight,
    })
    if (hsl.s < ACCENT_MIN_SATURATION) continue

    const bin = Math.min(ACCENT_HUE_BINS - 1, Math.floor(hsl.h * ACCENT_HUE_BINS))
    binWeight[bin] = (binWeight[bin] ?? 0) + weight
    binHue[bin] = (binHue[bin] ?? 0) + hsl.h * weight
  }

  let best = -1
  let bestWeight = 0
  for (let i = 0; i < ACCENT_HUE_BINS; i++) {
    const weight = binWeight[i] ?? 0
    if (weight > bestWeight) {
      bestWeight = weight
      best = i
    }
  }

  if (best < 0) {
    warnings.push({kind: 'noColoredMaterials'})
    return fallback()
  }

  return {
    color: new Color().setHSL(
      (binHue[best] ?? 0) / bestWeight,
      ACCENT_SATURATION,
      ACCENT_LIGHTNESS,
      SRGBColorSpace
    ),
    source: 'model',
    samples,
    warnings,
  }
}
